/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useEffect, useState } from 'react';

import { DYNAMED_TAB_LABEL, DynamedSection, hasDynamedGuide, loadDynamedGuideSections } from './dynamed';
import { DynamedContent } from './DynamedContent';
import styles from './DynamedTabContentPage.module.scss';

export type DynamedTabContentPageProps = {
  allowEditing?: boolean;
  documentId?: string;
  documentSlug?: string;
  documentVersionId: string;
  guideName?: string;
  pageId: string;
};

export function DynamedTabContentPage({
  allowEditing,
  documentId,
  documentSlug,
  documentVersionId,
  guideName,
  pageId,
}: DynamedTabContentPageProps) {
  const [sections, setSections] = useState<DynamedSection[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [failedToLoad, setFailedToLoad] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!guideName || !hasDynamedGuide(guideName)) {
      setSections(null);
      setFailedToLoad(false);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    setIsLoading(true);
    setFailedToLoad(false);

    void loadDynamedGuideSections(guideName).then((loadedSections) => {
      if (!isMounted) return;
      setSections(loadedSections);
      setFailedToLoad(!loadedSections);
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [guideName]);

  if (isLoading) {
    return <div className={styles.statusMessage}>Loading {DYNAMED_TAB_LABEL}...</div>;
  }

  if (!sections || sections.length === 0) {
    return (
      <div className={styles.statusMessage}>
        {failedToLoad ? `Failed to load ${DYNAMED_TAB_LABEL} content.` : `No ${DYNAMED_TAB_LABEL} content available.`}
      </div>
    );
  }

  return (
    <DynamedContent
      allowEditing={allowEditing}
      documentId={documentId}
      documentSlug={documentSlug}
      documentVersionId={documentVersionId}
      pageId={pageId}
      sections={sections}
    />
  );
}
